import {ENTITLEMENT_KEYS} from '@packages/domains/billing/constants';

import {defineAbilityConfig} from '../system/defineAbilityConfig';

/**
 * Determines whether a user can manage site billing (e.g., add a site plan).
 * This ability checks the 'manage' permission on the 'siteBilling' resource.
 */
export const canManageSiteBilling = defineAbilityConfig([
  {permission: ['manage', 'siteBilling']},
] as const);

/**
 * Determines whether a user can manage workspace billing.
 * This ability checks the 'manage' permission on the 'workspaceBilling' resource.
 */
export const canManageWorkspaceBilling = defineAbilityConfig([
  {permission: ['manage', 'workspaceBilling']},
] as const);

/**
 * Determines whether a user can manage either site or workspace billing.
 * This combined ability is useful for UI elements that should be shown
 * to users who can manage billing at either level.
 *
 * Note: This uses multiple configs which means if EITHER permission is granted,
 * the ability returns true (OR logic between configs).
 */
export const canManageSiteOrWorkspaceBilling = defineAbilityConfig([
  {permission: ['manage', 'siteBilling']},
  {permission: ['manage', 'workspaceBilling']},
] as const);

/**
 * Determines whether a user can read site billing information (e.g., view site plan).
 * This ability checks the 'read' permission on the 'siteBilling' resource.
 */
export const canReadSiteBilling = defineAbilityConfig([
  {permission: ['read', 'siteBilling']},
] as const);

/**
 * Determines whether a user can read workspace billing information.
 * This ability checks the 'read' permission on the 'workspaceBilling' resource.
 */
export const canReadWorkspaceBilling = defineAbilityConfig([
  {permission: ['read', 'workspaceBilling']},
] as const);

/**
 * Indicates whether the enterprise billing experience is enabled.
 * Used to block self-serve checkout flows in Designer.
 *
 * This ability returns `true` when the `ALLOW_ENTERPRISE_BILLING_EXPERIENCE` entitlement has access granted.
 *
 * @see getEntitlements.ts - Implements the fallback and override logic
 */
export const hasEnterpriseBillingExperience = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_ENTERPRISE_BILLING_EXPERIENCE],
} as const);

/**
 * Indicates whether Enterprise self-provisioning UI is enabled.
 * Backed by the `ALLOW_ENTERPRISE_SELF_PROVISIONING_UI` entitlement.
 */
export const hasEnterpriseSelfProvisioningUi = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_ENTERPRISE_SELF_PROVISIONING_UI],
} as const);

export const hasEnterpriseWorkspaceIdentifier = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.STATSIG_ENTERPRISE_WORKSPACE_IDENTIFIER],
} as const);

/**
 * Determines whether workspace access-request UI (e.g. the "Request Access"
 * button) should be suppressed. Backed by the `PREVENT_WORKSPACE_ACCESS_REQUESTS`
 * entitlement.
 *
 * @see enterpriseEntitlementBetaRegistry.ts - Defines the beta flag to entitlement mappings
 */
export const preventWorkspaceAccessRequests = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.PREVENT_WORKSPACE_ACCESS_REQUESTS],
} as const);

/**
 * Determines whether a workspace can exceed its AI credit limit — i.e. gets a
 * soft limit (warning) instead of a hard block when its balance reaches zero.
 *
 * Granted to enterprise plans via the `CAN_EXCEED_AI_CREDITS_LIMIT` entitlement;
 * ungranted (self-serve) means the workspace is hard-blocked at zero.
 */
export const canExceedAiCreditsLimit = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.CAN_EXCEED_AI_CREDITS_LIMIT],
} as const);
